import { useState } from "react";

import { trackRedeemClick } from "@loyalty/utils/trackers/redeemClick/trackRedeemClick";

import type { PrizeLocatorContextType, PrizeLocatorProps } from "@des/locators";
import { PrizeLocator } from "@des/locators";

import type { GetPrizesElementResponseType } from "@jti/rest-api-types";
import { useSelector } from "@jti/store/reduxHooks";

import type { RedeemDialogLabels } from "../RedeemDialog/RedeemDialog";
import { RedeemDialog } from "../RedeemDialog/RedeemDialog";
import type { SimplifiedPrizesProps } from "../SimplifiedPrizes/SimplifiedPrizes";

type FiltersTranslations = {
  filterKey: string;
  translatedFilterKey: string;
  image: string;
};

export type TrustedPlacesProps = {
  searchHeader: string;
  searchBar: string;
  rewardsText: string;
  searchButton: string;
  storesCount: string;
  getDirectionLabel: string;
  backToListLabel: string;
  categoryFilterTitle: string;
  venuePopUpOffersCount: string;
  currentPositionButtonText: string;
  filtersTranslations: FiltersTranslations[];
  // builder.io props
  isInBuilder?: boolean;
  logoUrl?: string;
  logoAlt?: string;
  isInEditor?: boolean;
  prizesPageUrl?: string;
} & Omit<PrizeLocatorProps, "activeStoreId" | "context" | "filtersEnabled"> &
  RedeemDialogLabels &
  Omit<
    SimplifiedPrizesProps,
    | "color"
    | "showNotAvailablePrizes"
    | "confirmationMessage"
    | "confirmationMessagePlural"
    | "isTeaser"
    | "teaserData"
    | "errorMessage"
    | "tierRestrictedLabel"
  >;
/**
 * Legacy Component for displaying trusted places with a prize locator and RedeemDialog for AEM only.
 * This component is deprecated and should be replaced with TrustedPlacesV2
 * kept for backward compatibility and migration script.
 **/

export const TrustedPlaces = (props: TrustedPlacesProps) => {
  const [dialogData, setDialogData] = useState<GetPrizesElementResponseType | Record<string, never>>({});
  const handleRedeemClick = (data: GetPrizesElementResponseType) => {
    setDialogData(data);
    trackRedeemClick(data);
  };

  const {
    points: { balance },
    tier: { name },
  } = useSelector((state) => state.loyaltyCore);

  const {
    costLabel,
    availablePointsLabel,
    balanceAfterRedemptionLabel,
    primaryCta,
    secondaryCta,
    dialogTitle,
    dialogTitleConfirmation,
    bottomText,
    pointsLabel,
    confirmationMessage,
    confirmationMessagePlural,
    codeLabel,
    codeInfo,
    emailHint,
    emailLabel,
    unitLabel,
    ctaLabel,
    loadMoreButtonLabel,
    searchHeader,
    searchBar,
    rewardsText,
    backToListLabel,
    searchButton,
    storesCount,
    getDirectionLabel,
    defaultLng,
    defaultLat,
    flotiqContentType,
    flotiqApiKey,
    filtersTranslations,
    categoryFilterTitle,
    noOffersText,
    pagination,
    venuePopUpOffersCount,
    currentPositionButtonText,
    newOfferDays,
    newLabel,
    oneDayLabel,
    daysLabel,
    outOfStockLabel,
    discoverUrlParent,
    discoverButtonText,
    quantityAriaLabel,
    increaseAriaLabel,
    decreaseAriaLabel,
  } = props;

  const context: PrizeLocatorContextType = {
    labels: {
      loadMoreLabel: loadMoreButtonLabel,
      redeemButtonLabel: ctaLabel,
      backToListLabel,
      getDirectionLabel,
      searchHeader,
      pointsLabel,
      rewardsText,
      searchBar,
      searchButton,
      storesCount,
      categoryFilterTitle,
      noOffersText,
      venuePopUpOffersCount,
      currentPositionButtonText,
      newOfferDays,
      newLabel,
      oneDayLabel,
      daysLabel,
      outOfStockLabel,
      discoverButtonText,
    },
    pagination,
    userPointsBalance: balance,
    handleRedeemClick,
    tier: name,
    discoverUrlParent,
  };

  const resetDialogData = () => setDialogData({});

  return (
    <>
      <PrizeLocator
        context={context}
        defaultLat={defaultLat}
        defaultLng={defaultLng}
        flotiqApiKey={flotiqApiKey}
        flotiqContentType={flotiqContentType}
        filtersTranslations={filtersTranslations}
      />

      <RedeemDialog
        isOpened={!!dialogData?.prize_uuid}
        pointsBalance={balance}
        prizeData={dialogData}
        onCloseDialog={resetDialogData}
        labels={{
          costLabel,
          availablePointsLabel,
          balanceAfterRedemptionLabel,
          primaryCta,
          secondaryCta,
          dialogTitle,
          dialogTitleConfirmation,
          bottomText,
          pointsLabel,
          confirmationMessage,
          confirmationMessagePlural,
          codeLabel,
          codeInfo,
          emailHint,
          emailLabel,
          unitLabel,
          quantityAriaLabel,
          increaseAriaLabel,
          decreaseAriaLabel,
        }}
      />
    </>
  );
};
